/**
 * When DiffResult.unchanged equals this value, both contexts point to the
 * same version — no diff was computed.
 */
export const SAME_VERSION_SENTINEL = -1;

export const VERSION_CONTEXTS = {
  EDIT: 'edit',
  TEST: 'test',
  LIVE: 'live',
  READ: 'read'
} as const;

// Publish progression: lower index → higher index.
// edit (lowest) → test → live (highest published). Mirrors
// PUBLISH_POLICIES.etl.order in @agilemd/core-content (server-only).
export const VERSION_CONTEXT_PUBLISH_ORDER = [
  VERSION_CONTEXTS.EDIT,
  VERSION_CONTEXTS.TEST,
  VERSION_CONTEXTS.LIVE
] as const;

export type VersionContext = (typeof VERSION_CONTEXTS)[keyof typeof VERSION_CONTEXTS];

/**
 * Closed enum of mapping-table types. Single source of truth — apps and
 * packages must import from here rather than redefining or hardcoding the
 * string literals. The matching `MappingTableType` alias lives in
 * `@agilemd/types`.
 *
 * Adding a type here is NOT complete on its own: every type needs a matching
 * lookupKey+dimensions uniqueness constraint on `content_mapping_entries` (a
 * partial unique index for facility-unscoped rows + a GiST exclusion for
 * facility-scoped rows). Add a migration like
 * `apps/api/src/migrations/2026.07.15.mapping_entries_clinical_value_unique.ts`
 * for the new type, or duplicate rows will slip through.
 */
export const MAPPING_TABLE_TYPES = {
  EHR_DATA_DESTINATIONS: 'ehr-data-destinations',
  VALUE_MAPPINGS: 'value-mappings',
  HL7_CONFIG: 'hl7-config',
  CLINICAL_CODES: 'clinical-codes'
} as const;

/**
 * Wire-format discriminator for mapping-table resolution responses. Produced
 * by `core-content`'s resolver, surfaced unchanged through the `/v4/.../resolve`
 * HTTP endpoint, and consumed by runtime callers (e.g. `apps/org` answers
 * controller) to branch on hit/miss.
 */
export const MAPPING_RESOLUTION_HITS = {
  MISS: 'miss',
  EXACT: 'exact'
} as const;

/**
 * Rule kinds for a `value-mappings` entry, mirrored by the zod schema in
 * `@agilemd/schema-version` and the `ValueMappingRule` union in `@agilemd/types`.
 * Consumers (resolver, hl7-processor, admin-console editor) import from here
 * rather than hardcoding the discriminant strings.
 */
export const VALUE_MAPPING_RULE_TYPES = {
  EXACT: 'exact',
  WILDCARD: 'wildcard',
  RANGE: 'range'
} as const;

export type ValueMappingRuleType =
  (typeof VALUE_MAPPING_RULE_TYPES)[keyof typeof VALUE_MAPPING_RULE_TYPES];

/**
 * Vocabulary of the value-mappings rule CSV: the sentinels an output cell can
 * carry, the prefix for an object entry's per-key columns, and the delimiter
 * inside an array cell. The parser, the serializer and the admin console's
 * column reference all have to agree on these, so they live here rather than
 * being restated per module.
 */
export const RULE_CSV_FORMAT = {
  /** Output cell that maps to nothing, the `output: null` drop case. */
  DROP: 'DROP',
  /** Output cell that echoes the matched input (wildcard/range only). */
  PASSTHROUGH: 'PASSTHROUGH',
  OUTPUT_KEY_PREFIX: 'output.',
  ARRAY_DELIMITER: '|'
} as const;

/**
 * Op kinds in a value-mappings rule CSV import: what one row does to the
 * entry's existing rules. The admin-console server classifies, the browser
 * renders and selects, so both sides read the vocabulary from here.
 */
export const RULE_IMPORT_OP_KINDS = {
  ADD: 'add',
  CHANGE: 'change',
  REMOVE: 'remove'
} as const;

export type RuleImportOpKind = (typeof RULE_IMPORT_OP_KINDS)[keyof typeof RULE_IMPORT_OP_KINDS];

export const MAPPING_TABLE_LIST_PARAMS = {
  INCLUDE_CONTENT_HASH: 'include-content-hash'
} as const;

/**
 * Result types a `value-mappings` entry can declare. The zod schema in
 * `@agilemd/schema-version` re-exports this and builds its enum from it; the
 * `ValueMappingResultType` alias in `@agilemd/types` matches these values.
 * `string`/`number` are the only types a passthrough range rule can carry.
 */
export const VALUE_MAPPING_RESULT_TYPES = {
  STRING: 'string',
  NUMBER: 'number',
  OBJECT: 'object',
  STRING_ARRAY: 'string[]',
  NUMBER_ARRAY: 'number[]'
} as const;

export type ValueMappingResultTypeValue =
  (typeof VALUE_MAPPING_RESULT_TYPES)[keyof typeof VALUE_MAPPING_RESULT_TYPES];

export const WEB_APPS = {
  AUTHOR: 'author',
  VIEWER: 'viewer',
  REVIEWER: 'reviewer',
  REPORTS: 'reports',
  ADMIN_CONSOLE: 'admin-console',
  ATLAS: 'atlas',
  ECART: 'ecart'
} as const;
export const CORE_TENANT_ID = 'core';

export const RESOURCE_TYPES = {
  APP: 'app',
  ACCOUNT: 'account',
  FILE_FILTER: 'filter_view',
  ORGANIZATION: 'organization',
  ORGANIZATION_LAUNCHER: 'organization-launcher',
  MODULE: 'module',
  FILE: 'file',
  GROUP: 'group',
  POLICY: 'policy',
  SYSTEM_BANNER: 'system_banner',
  TENANT: 'tenant'
} as const;
export const GROUP_ROLES = Object.freeze({
  APP: {
    OWNER: 'owner',
    USER: 'user'
  },
  MODULE: {
    ADMIN: 'admin',
    READ: 'read',
    EDIT_FILES: 'edit_files',
    CREATE_FILES: 'create_files',
    DELETE_FILES: 'delete_files',
    PUBLISH_TEST: 'publish_test',
    PUBLISH_LIVE: 'publish_live'
  },
  GROUP: {
    READ: 'read',
    ADMIN: 'admin'
  },
  ORGANIZATION: {
    ADMIN: 'admin',
    ACCESS_FORUM: 'access_forum',
    USE_ADMIN_CONSOLE: 'use_admin_console',
    REPORTER: 'reporter',
    SUPPORT_MANAGER: 'support_manager',
    ORG_MANAGER: 'org_manager',
    CUSTOMER_MAPPINGS_MANAGER: 'customer_mappings_manager',
    POPULATION_EXPLORER: 'population_explorer'
  }
});
export const ORG_ROLES = Object.freeze({
  REPORTER: 'reporter',
  SUPPORT_MANAGER: 'support_manager',
  ORG_MANAGER: 'org_manager',
  CUSTOMER_MAPPINGS_MANAGER: 'customer_mappings_manager',
  POPULATION_EXPLORER: 'population_explorer'
});
export const ORG_ROLE_LABELS = Object.freeze({
  [ORG_ROLES.REPORTER]: 'Report Viewer',
  [ORG_ROLES.SUPPORT_MANAGER]: 'Support Manager',
  [ORG_ROLES.ORG_MANAGER]: 'Organization Manager',
  [ORG_ROLES.CUSTOMER_MAPPINGS_MANAGER]: 'Customer Mappings Manager',
  [ORG_ROLES.POPULATION_EXPLORER]: 'Population Explorer'
});
export const ORG_ROLE_GROUP_NAMES = Object.freeze({
  [ORG_ROLES.REPORTER]: 'Report Viewers',
  [ORG_ROLES.SUPPORT_MANAGER]: 'Support Managers',
  [ORG_ROLES.ORG_MANAGER]: 'Organization Managers',
  [ORG_ROLES.CUSTOMER_MAPPINGS_MANAGER]: 'Customer Mappings Managers',
  [ORG_ROLES.POPULATION_EXPLORER]: 'Population Explorers'
});
export const ACCOUNT_TYPES = Object.freeze({
  ANONYMOUS: 'anonymous',
  EMPLOYEE: 'employee',
  USER: 'user',
  SERVICE_ACCOUNT: 'service_account'
});
export const AUTHORIZATION_ACTIONS = Object.freeze({
  ACCOUNT: {
    READ: 'can_read',
    EDIT: 'can_edit'
  },
  FILE_FILTER: {
    EDIT: 'can_edit',
    DELETE: 'can_delete'
  },
  ORGANIZATION: {
    CREATE_SYSTEM_BANNER: 'can_create_system_banner',
    CREATE_GROUP: 'can_create_group',
    CREATE_KEY: 'can_create_key',
    CREATE_FILE_FILTER: 'can_create_file_filter',
    LIST_EVENTS: 'can_list_events',
    LIST_FILE_FILTERS: 'can_list_file_filters',
    LIST_RESOURCES: 'can_list_resources',
    READ: 'can_read',
    READ_MAPPING_TABLES: 'can_read_mapping_tables',
    VIEW_DASHBOARDS: 'can_view_dashboards',
    MANAGE_EHR_DATA_DESTINATIONS_MAPPING_TABLE: 'can_manage_ehr_data_destinations_mapping_table',
    MANAGE_VALUE_MAPPINGS_MAPPING_TABLE: 'can_manage_value_mappings_mapping_table',
    MANAGE_HL7_CONFIG_MAPPING_TABLE: 'can_manage_hl7_config_mapping_table',
    MANAGE_CLINICAL_CODES_MAPPING_TABLE: 'can_manage_clinical_codes_mapping_table',
    MANAGE_REPORTER_PERMISSIONS: 'can_manage_reporter_permissions',
    MANAGE_SUPPORT_MANAGER_PERMISSIONS: 'can_manage_support_manager_permissions',
    MANAGE_ORG_MANAGER_PERMISSIONS: 'can_manage_org_manager_permissions',
    MANAGE_CUSTOMER_MAPPINGS_MANAGER_PERMISSIONS:
      'can_manage_customer_mappings_manager_permissions',
    MANAGE_POPULATION_EXPLORER_PERMISSIONS: 'can_manage_population_explorer_permissions',
    MANAGE_REVIEWS: 'can_manage_reviews',
    MANAGE_OVERVIEW_SETTINGS: 'can_manage_overview_settings',
    READ_MEMBERS: 'can_read_members',
    USE_POPULATION_EXPLORER: 'can_use_population_explorer',
    MANAGE_SUPPORT_CONTACTS: 'can_manage_support_contacts',
    MANAGE_FHIR_API_PERMISSIONS: 'can_manage_fhir_api_permissions',
    MANAGE_EHR_CONTEXT_LINKS: 'can_manage_ehr_context_links',
    RESET_MEMBER_MFA: 'can_reset_member_mfa',
    MANAGE_REVIEWER_GROUPS: 'can_manage_reviewer_groups',
    USE_ADMIN_CONSOLE: 'can_use_admin_console',
    USE_HL7_PROCESSOR: 'can_use_hl7_processor',
    AUTHOR: 'can_author',
    USE_REVIEWER: 'can_use_reviewer'
  },
  GROUP: {
    ATTACH_APP: 'can_attach_app',
    ATTACH_MODULE: 'can_attach_module',
    ATTACH_CHILD_GROUP: 'can_attach_child_group',
    ATTACH_THIS_GROUP: 'can_action_this_group',
    CREATE_LAUNCH_KEY: 'can_create_launch_key',
    CREATE_READ_LINK: 'can_create_read_link',
    CREATE_MODULE: 'can_create_module',
    DELEGATE_PERMISSIONS: 'can_delegate_permissions',
    EDIT_LAUNCH_KEY: 'can_edit_launch_key',
    EDIT_READ_LINK: 'can_edit_read_link',
    EDIT_PERMISSIONS: 'can_edit_permissions',
    EDIT: 'can_edit',
    INVITE_MEMBER: 'can_invite_member',
    LIST_AUDIT_EVENTS: 'can_list_audit_events',
    MODIFY_PERMISSIONS: 'can_modify_permissions',
    READ_MEMBERS: 'can_read_members',
    READ: 'can_read',
    READ_LAUNCH_KEY: 'can_read_launch_key',
    READ_READ_LINK: 'can_read_read_link',
    REMOVE_APP: 'can_remove_app',
    REMOVE_CHILD_GROUP: 'can_remove_child_group',
    REMOVE: 'can_remove',
    REMOVE_MEMBER: 'can_remove_member',
    REMOVE_MODULE: 'can_remove_module',
    REMOVE_LAUNCH_KEY: 'can_remove_launch_key',
    REMOVE_READ_LINK: 'can_remove_read_link'
  },
  APP: {
    USE: 'can_use',
    EDIT: 'can_edit',
    DELETE: 'can_delete',
    PUBLISH_TEST: 'can_publish_test',
    PUBLISH_LIVE: 'can_publish_live'
  },
  MODULE: {
    READ: 'can_read',
    LIST: 'can_list',
    EDIT_FILE: 'can_edit_file',
    EDIT_FOLDER: 'can_edit_folder',
    EDIT: 'can_edit',
    CREATE_FILE: 'can_create_file',
    DELETE_FILE: 'can_delete_file',
    PUBLISH_TEST_FILE: 'can_publish_test_file',
    PUBLISH_LIVE_FILE: 'can_publish_live_file',
    DELETE: 'can_delete',
    DELEGATE_PERMISSIONS: 'can_delegate_permissions'
  },
  FILE: {
    READ: 'can_read',
    EDIT: 'can_edit',
    REVIEW: 'can_review',
    EDIT_REVIEW_SETTINGS: 'can_edit_review_settings',
    EDIT_REVIEWERS: 'can_edit_reviewers',
    CREATE: 'can_create',
    DELETE: 'can_delete',
    PUBLISH_TEST: 'can_publish_test',
    PUBLISH_LIVE: 'can_publish_live',
    CREATE_SNAPSHOT: 'can_create_snapshot',
    WRITE_POPULATION_CRITERIA: 'can_write_population_criteria',
    PROMOTE_POPULATION_CRITERIA: 'can_promote_population_criteria'
  },
  SYSTEM_BANNER: {
    EDIT: 'can_edit',
    DELETE: 'can_delete'
  },
  TENANT: {
    ACCESS_FORUM: 'can_access_forum',
    DELEGATE_ATTRIBUTION: 'can_delegate_attribution',
    RESOLVE_EMAIL: 'can_resolve_email'
  }
});

export const getManageRolePermission = (role: string): string => `can_manage_${role}_permissions`;

// Permissive on purpose (the mail provider is the arbiter of deliverability); used wherever an
// invitee address is accepted so every invite path agrees on what counts as an email.
export const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_MAX_LENGTH = 255;

// A calendar date on the wire (`YYYY-MM-DD`): shape only, the consumer checks the day exists.
export const CALENDAR_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
export const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';

// Shared so the roster editor, the invite modal and the API all cap the author's note identically.
export const REVIEW_INSTRUCTIONS_MAX_LENGTH = 1000;

// The one-off note on a reminder email. Separate from the instructions cap on purpose: the two are
// independent limits that happen to agree today.
export const REVIEW_REMINDER_NOTE_MAX_LENGTH = 1000;

// Postgres LIKE/ILIKE treats % and _ as wildcards; a user-supplied value is a literal.
export function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, '\\$&');
}
